// @flow
import {type SagaIterator} from 'redux-saga';
import {FULFILLED} from 'redux-promise-middleware';

import {LOCATION_CHANGED, CHECKOUT} from 'constants/actions';
import {
    QUICK_CHECKOUT_ACTIVATED_FLAG_NAME,
    QUICK_CHECKOUT_WITHOUT_RECOVERY_FLAG_NAME,
} from 'constants/quickCheckout';

import {takeEvery, call, put, putResolve, select} from 'redux-saga/effects';
import {localStore} from 'libs/storage/storage';
import {getItemsCount} from 'actions/cart/cart';
import {recoverQuote} from 'features/quickCheckout/reducers/checkout/quick/quick';
import {isCheckoutPage, isSuccessPage} from 'features/quickCheckout/selectors/router/router';
import {toggle} from 'features/quickCheckout/reducers/checkout/buyNowPopup/buyNowPopup';
import {createEventChannel} from 'libs/eventChannel/eventChannel';

/**
 * Removes quick checkout flag from session storage
 */
export function* removeQuickCheckoutFlag(): SagaIterator {
    yield call([localStore, 'set'], QUICK_CHECKOUT_ACTIVATED_FLAG_NAME, false);
}

/**
 * Removes quick checkout without recovery flag from session storage
 */
export function* removeQuickCheckoutFlagWithoutRecovery(): SagaIterator {
    yield call([localStore, 'set'], QUICK_CHECKOUT_WITHOUT_RECOVERY_FLAG_NAME, false);
}

/**
 * Recovers regular quote if quick checkout quote is active
 */
export function* recoverRegularQuote(): SagaIterator {
    const isQuickCheckoutActivated = yield call(
        [localStore, 'get'],
        QUICK_CHECKOUT_ACTIVATED_FLAG_NAME,
    );
    const isOnCheckoutPage = yield select(isCheckoutPage);
    const isOnSuccessPage = yield select(isSuccessPage);

    if (isQuickCheckoutActivated && !isOnCheckoutPage && !isOnSuccessPage) {
        const recoverQuoteAction = yield call(recoverQuote);
        yield putResolve(recoverQuoteAction);

        yield put(getItemsCount());
        // we should reset buy now popup
        yield put(toggle(false));
    }
}

/**
 * Removes all the recovery flags if quick checkout without recovery
 */
export function* onPlaceOrder(): SagaIterator {
    const isQuickCheckoutWithoutRecovery = yield call(
        [localStore, 'get'],
        QUICK_CHECKOUT_WITHOUT_RECOVERY_FLAG_NAME,
    );

    if (isQuickCheckoutWithoutRecovery) {
        yield call(removeQuickCheckoutFlag);
        yield call(removeQuickCheckoutFlagWithoutRecovery);
    }
}

/**
 * Recovery saga
 */
export function* recoverySaga(): SagaIterator {
    const nativeRedirect = yield call(createEventChannel, 'postMessage:redirect');

    yield takeEvery(`${recoverQuote}_${FULFILLED}`, removeQuickCheckoutFlag);
    yield takeEvery(`${recoverQuote}_${FULFILLED}`, removeQuickCheckoutFlagWithoutRecovery);
    yield takeEvery(LOCATION_CHANGED, recoverRegularQuote);
    yield takeEvery(nativeRedirect, recoverRegularQuote);
    yield takeEvery(`${CHECKOUT.SAVE_ORDER_SUCCESS}`, onPlaceOrder);
}
